import React, { memo, useCallback, useRef, useState } from 'react';
import { Popper } from '@mui/material';
import { PopperUnstyledProps } from '@mui/base';
import { merge } from 'lodash-es';

import { ICalendarDayEvent, ICalendarOffset } from '~/Screens/Calendar/interfaces';
import { PositionedEvent } from '~/Screens/Calendar/CalendarView/components';
import { makeStyles } from '~/UIKit/Style';
import { EVENT_Z_INDEX } from '~/Screens/Calendar/constants';
import { useEventTypePickerListItems } from '~/Screens/Events/EventCreate/EventTypePicker/useEventTypePickerListItems';
import { ItemCreationPicker } from '~/Components/ItemCreation/ItemCreationPicker';
import { WHITE } from '~/Constants/Colors.constants';
import { BOX_SHADOW_ARROW, BOX_SHADOW_UNDIRECTED } from '~/Constants/CSS.constants';
import { EventType } from '~/Models/DTO/Event.model';
import { useCreateCalendarEvent } from './useCreateCalendarEvent';
import { EventCreationForm } from '~/Screens/Calendar/CalendarView/components/CreatingEvent/EventCreationForm/EventCreationForm';
import { EventCreationStep } from '~/Screens/Calendar/CalendarView/components/CreatingEvent/types';

const arrowSize = 1.7;
const useStyles = makeStyles(
  () => ({
    container: {
      zIndex: EVENT_Z_INDEX + 2,
      position: 'absolute',
    },
    popper: {
      zIndex: EVENT_Z_INDEX + 2,
      '&[data-popper-placement*="bottom"] [class*=popperArrow]': {
        top: `-${arrowSize / 8}em`,
        left: 0,
        '&::before': {
          transformOrigin: '0 100%',
        },
      },
      '&[data-popper-placement*="top"] [class*=popperArrow]': {
        bottom: `-${arrowSize / 8}em`,
        left: 0,
        '&::before': {
          transformOrigin: '100% 0',
        },
      },
      '&[data-popper-placement*="right"] [class*=popperArrow]': {
        left: `-${arrowSize / 8}em`,
        height: `${arrowSize}em`,
        width: `${(arrowSize * 2) / 3}em`,
        '&::before': {
          transformOrigin: '100% 100%',
        },
      },
      '&[data-popper-placement*="left"] [class*=popperArrow]': {
        right: `-${arrowSize / 8}em`,
        height: `${arrowSize}em`,
        width: `${(arrowSize * 2) / 3}em`,
        '&::before': {
          transformOrigin: '0 0',
        },
      },
    },
    popperArrow: {
      overflow: 'hidden',
      position: 'absolute',
      width: `${arrowSize}em`,
      height: `${(arrowSize * 2) / 3}em`,
      boxSizing: 'border-box',
      color: WHITE,
      '&::before': {
        content: '""',
        margin: 'auto',
        display: 'block',
        width: '100%',
        height: '100%',
        boxShadow: BOX_SHADOW_ARROW,
        backgroundColor: 'currentColor',
        transform: 'rotate(45deg)',
      },
    },
    eventTypePicker: {
      padding: '3px 10px',
    },
    popperContent: {
      margin: arrowSize * 8.5,
      background: WHITE,
      boxShadow: BOX_SHADOW_UNDIRECTED,
      borderRadius: 3,
      minWidth: 324,
    },
  }),
  { name: 'CreatingEvent' },
);

export const CreatingEvent = memo(
  ({
    top,
    offset,
    startOfPeriod,
    calendarDayEvent,
    contentPlacement,
  }: {
    top: number;
    offset: ICalendarOffset;
    startOfPeriod: string;
    calendarDayEvent: ICalendarDayEvent;
    contentPlacement: PopperUnstyledProps['placement'];
  }) => {
    const { classes } = useStyles();
    const eventRef = useRef(null);
    const [arrowEl, setArrowEl] = useState<HTMLElement | null>(null);
    const {
      onCancel,
      newEvent,
      setNewEvent,
      creationStep,
      setCreationStep,
    } = useCreateCalendarEvent({
      offset,
      startOfPeriod,
      calendarDayEvent,
    });

    const onEventTypeClicked = useCallback(
      (eventType: EventType) => {
        const event = { type: eventType };
        setNewEvent((prev) => merge({}, prev, { event }));
        setCreationStep(EventCreationStep.FILL_FORM);
      },
      [setNewEvent, setCreationStep],
    );
    const listItems = useEventTypePickerListItems({ onEventTypeClicked });

    if (!newEvent) {
      return null;
    }
    const { position, event, isLoading } = newEvent;

    return (
      <div className={classes.container} style={{ top }}>
        <Popper
          open={![EventCreationStep.SUBMITTING, EventCreationStep.CREATED].includes(creationStep)}
          anchorEl={eventRef.current}
          placement={contentPlacement}
          className={classes.popper}
          disablePortal
          modifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: 'document',
                padding: 5,
              },
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                padding: 5,
                rootBoundary: 'document',
              },
            },
            {
              name: 'arrow',
              enabled: true,
              options: {
                element: arrowEl,
              },
            },
          ]}
          nonce={undefined}
          onResize={undefined}
          onResizeCapture={undefined}
        >
          <span className={classes.popperArrow} ref={setArrowEl} />
          <div className={classes.popperContent}>
            {creationStep === EventCreationStep.SELECT_EVENT_TYPE && (
              <ItemCreationPicker listItems={listItems} className={classes.eventTypePicker} />
            )}
            {creationStep === EventCreationStep.FILL_FORM && (
              <EventCreationForm
                event={event}
                onCancel={onCancel}
                setNewEvent={setNewEvent}
                setCreationStep={setCreationStep}
              />
            )}
          </div>
        </Popper>
        <PositionedEvent
          event={event}
          eventRef={eventRef}
          position={position}
          isLoading={isLoading}
        />
      </div>
    );
  },
);
